import { Loader2 } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { BackLink, ErrorBanner, getApiErrorMessage, PasswordHint, SignInFooter } from './auth-form-helpers';

interface ProfessionalSignupFormProps {
  onBack: () => void;
  onSignIn: () => void;
  onSuccess: () => void;
}

export function ProfessionalSignupForm({ onBack, onSignIn, onSuccess }: ProfessionalSignupFormProps) {
  const { signupProfessional } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inFlight = useRef(false);
  const orgInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    orgInputRef.current?.focus();
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setError('');
    setIsLoading(true);
    try {
      await signupProfessional({
        name,
        email,
        password,
        organization_name: organizationName,
      });
      onSuccess();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not create your clinic account.'));
    } finally {
      inFlight.current = false;
      setIsLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <BackLink onClick={onBack} />
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        {error && <ErrorBanner message={error} />}
        <div className="flex flex-col gap-2">
          <Label htmlFor="professional-org">Clinic or organization name</Label>
          <Input
            ref={orgInputRef}
            id="professional-org"
            required
            value={organizationName}
            autoComplete="organization"
            placeholder="Smith Family Practice"
            onChange={(e) => setOrganizationName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="professional-name">Your name</Label>
          <Input
            id="professional-name"
            required
            value={name}
            autoComplete="name"
            placeholder="Dr Jane Doe"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="professional-email">Email</Label>
          <Input
            id="professional-email"
            required
            type="email"
            value={email}
            autoComplete="email"
            placeholder="you@example.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="professional-password">Password</Label>
          <Input
            id="professional-password"
            required
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
          />
          <PasswordHint />
        </div>
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create clinic account
        </Button>
      </form>
      <SignInFooter onSignIn={onSignIn} />
    </div>
  );
}
